
// src/components/render-layers/render-layer-manager.ts
import { RenderLayerConfig, RenderLayerConfigIn } from './render-layer-defaults';
import { RenderLayerNode } from './render-layer-node.type';
import { RenderLayerNodes } from './render-layer-nodes.type';
import { RenderLayer } from './render-layer.enum';
import { sortRenderLayerNodes } from './sort-render-layer-nodes';
import { ServiceBase } from '../types/service-event-bridge.type';

export class RenderLayerManagerClass extends ServiceBase {
	private nodes = new Map<string, RenderLayerNode>();
	private layerCache = new Map<RenderLayer, RenderLayerNode[]>();
	private cachedSortedNodes: RenderLayerNode[] = [];
	private isDirty = false;
	private version = 0; // Simple version counter for change detection
	private renderCount = 0; // Track total render operations
	private subscribers = new Set<() => void>(); // Track subscribers for notifications

	readonly config: RenderLayerConfig;

	constructor (dataIn: RenderLayerConfigIn) {
		super();
		this.config = dataIn as RenderLayerConfig;
		this.nodes = new Map<string, RenderLayerNode>();
		this.layerCache = new Map<RenderLayer, RenderLayerNode[]>();
		this.isDirty = false;
		this.version = 0; // Simple version counter for change detection
	}

	register (id: string, node: Omit<RenderLayerNode, 'id'>): void {
		this.nodes.set(id, { ...node, id });
		this.invalidateCache();
		this.version++; // Increment version when nodes change
	}

	unregister (id: string): void {
		const existed = this.nodes.has(id);
		this.nodes.delete(id);
		if (existed) {
			this.invalidateCache();
			this.version++; // Increment version when nodes change
		}
	}

	getSortedNodes (): RenderLayerNode[] {
		if (!this.isDirty && this.cachedSortedNodes.length > 0) {
			// Return cached result - same reference for caching test
			return this.cachedSortedNodes;
		}

		const sorted = sortRenderLayerNodes(this.nodes);
		this.updateCache(sorted);
		this.cachedSortedNodes = sorted;
		return sorted;
	}

	getNodesByLayer (layer: RenderLayer): RenderLayerNode[] {
		if (!this.isDirty && this.layerCache.has(layer)) {
			return this.layerCache.get(layer) || [];
		}

		const nodes = Array.from(this.nodes.values())
			.filter((node) => node.layer === layer)
			.sort((a, b) => a.priority - b.priority);
		this.updateCache(Array.from(this.nodes.values()));
		return nodes;
	}

	getLayerStats (): Map<RenderLayer, number> {
		const stats = new Map<RenderLayer, number>();
		for (const layer of Object.values(RenderLayer)) {
			if (typeof layer === 'number') {
				const count = this.getNodesByLayer(layer).length;
				if (count > 0) {
					stats.set(layer, count);
				}
			}
		}
		return stats;
	}

	getPerformanceMetrics (): {
		totalNodes: number;
		layerCounts: Map<RenderLayer, number>;
		cacheHitRate: number;
		renderCount: number;
		} {
		// Return basic metrics
		return {
			totalNodes: this.nodes.size,
			layerCounts: this.getLayerStats(),
			cacheHitRate: this.isDirty ? 0 : 1,
			renderCount: this.renderCount
		};
	}

	// Get current version for change detection
	getVersion (): number {
		return this.version;
	}

	// Force a re-render by incrementing version (useful for programmatic updates)
	forceReRender (): void {
		this.version++;
		console.log('RenderLayerManager: Force re-render triggered, new version:', this.version);
		// Notify subscribers so throttling mechanism can work
		this._notifySubscribers();
	}

	// Update configuration and publish changes
	updateConfig (newConfig: Partial<RenderLayerConfig>): void {
		// Note: config is readonly, so we can't actually update it
		// This method is here for API consistency and to publish config change events
		// Publish config changes to controller
		this.publish('render-layer-config', 'updated', { ...this.config, ...newConfig });
	}

	// Increment render count (called when actual rendering occurs)
	incrementRenderCount (): void {
		this.renderCount++;
	}

	// Get current render count
	getRenderCount (): number {
		return this.renderCount;
	}

	// Reset render count to 0
	resetRenderCount (): void {
		this.renderCount = 0;
		console.log('RenderLayerManager: Render count reset to 0');
	}

	// Get total node count
	getNodeCount (): number {
		return this.nodes.size;
	}

	// Check if a layer is empty
	isLayerEmpty (layer: RenderLayer): boolean {
		return this.getNodesByLayer(layer).length === 0;
	}

	// Clear all nodes from a specific layer
	clearLayer (layer: RenderLayer): void {
		const nodesToRemove = this.getNodesByLayer(layer);
		nodesToRemove.forEach((node) => this.nodes.delete(node.id));
		this.invalidateCache();
		this.version++; // Increment version when nodes change
	}

	subscribe (callback: () => void): () => void {
		// Add subscriber
		this.subscribers.add(callback);

		// Return unsubscribe function
		return (): void => {
			this.subscribers.delete(callback);
		};
	}

	private invalidateCache (): void {
		this.isDirty = true;
		this.layerCache.clear();
		this.cachedSortedNodes = [];
	}

	private updateCache (sortedNodes: RenderLayerNode[]): void {
		this.layerCache.clear();
		for (const node of sortedNodes) {
			if (!this.layerCache.has(node.layer)) {
				this.layerCache.set(node.layer, []);
			}
			this.layerCache.get(node.layer)!.push(node);
		}
		this.isDirty = false;
	}

	private _notifySubscribers (): void {
		// Notify all subscribers about the version change
		this.subscribers.forEach((callback) => {
			try {
				callback();
			} catch (error) {
				console.warn('RenderLayerManager: Error in subscriber callback:', error);
			}
		});
		
		// Publish render layer changes to controller
		this.publish('render-layer', 'updated', {
			version: this.version,
			nodeCount: this.nodes.size,
			isDirty: this.isDirty,
			renderCount: this.renderCount
		});
	}
}

export const renderLayerManager = new RenderLayerManagerClass({});
